import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import {
  dismissPoEmail,
  downloadPoEmail,
  type PoPendingEmail,
} from '../../lib/demPoEmailMonitor';
import { emlBase64ToFile } from '../../lib/demPoEmailParse';

type PoEmailQueueModalProps = {
  open: boolean;
  onClose: () => void;
  onProcessFile: (file: File) => void;
  emails: PoPendingEmail[];
  loading?: boolean;
  error?: string | null;
  lastSync?: string;
  onReload: (silent?: boolean) => Promise<void>;
  onDismissed?: () => void;
};

export function PoEmailQueueModal({
  open,
  onClose,
  onProcessFile,
  emails,
  loading = false,
  error: externalError = null,
  lastSync = '—',
  onReload,
  onDismissed,
}: PoEmailQueueModalProps) {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const error = localError ?? externalError;

  useEffect(() => {
    if (!open) return;
    void onReload();
    const timer = window.setInterval(() => {
      void onReload(true);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [open, onReload]);

  useEffect(() => {
    if (selectedId && !emails.some((row) => row.id === selectedId)) {
      setSelectedId(null);
    }
  }, [emails, selectedId]);

  const handleDismiss = async () => {
    if (!selectedId) return;
    const ok = window.confirm('Dismiss this email from the queue?');
    if (!ok) return;
    setLocalError(null);
    try {
      await dismissPoEmail(selectedId);
      setSelectedId(null);
      await onReload(true);
      onDismissed?.();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Failed to dismiss email.');
    }
  };

  const handleProcess = async () => {
    if (!selectedId) return;
    setProcessingId(selectedId);
    setLocalError(null);
    try {
      const downloaded = await downloadPoEmail(selectedId);
      const emlFile = emlBase64ToFile(downloaded.emlBase64, downloaded.subject);
      onProcessFile(emlFile);
      onClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to process email.';
      if (message === 'ALREADY_READ') {
        setLocalError('Too late — another user may have already taken this order.');
      } else {
        setLocalError(message);
      }
      await onReload(true);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Email orders queue" wide>
      <div className="dem-po-email-queue">
        <div className="dem-po-email-queue__toolbar">
          <div className="dem-po-email-queue__actions">
            <button type="button" className="btn-secondary btn-sm" disabled={loading} onClick={() => void onReload()}>
              Refresh
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!selectedId || processingId != null}
              onClick={() => void handleDismiss()}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={!selectedId || processingId != null}
              onClick={() => void handleProcess()}
            >
              {processingId ? 'Downloading…' : 'Process order'}
            </button>
          </div>
          <p className="dem-po-email-queue__sync">Inbox last synced: {lastSync}</p>
        </div>

        {error ? <p className="alert alert-error">{error}</p> : null}

        <div className="dem-po-email-queue__table-wrap">
          <div className="dem-po-email-queue__head" aria-hidden>
            <span>Received</span>
            <span>Subject</span>
          </div>
          {loading && emails.length === 0 ? (
            <p className="dem-po-email-queue__empty">Loading inbox…</p>
          ) : emails.length === 0 ? (
            <p className="dem-po-email-queue__empty">No pending email orders.</p>
          ) : (
            emails.map((email) => (
              <button
                key={email.id}
                type="button"
                className={`dem-po-email-queue__row${selectedId === email.id ? ' dem-po-email-queue__row--selected' : ''}`}
                onClick={() => setSelectedId(email.id)}
              >
                <span>{email.received}</span>
                <span>{email.subject}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
